import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Plus, Edit2, Trash2, Loader2, Lock, Users, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const Roles = () => {
  const navigate = useNavigate();
  const perms = detectPerms();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/roles`, getAuthHeaders());
      setRoles(res.data);
    } catch {
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoles(); }, []);

  const handleDelete = async (id) => {
    try {
      setDeleteError('');
      await axios.delete(`${BACKEND_URL}/api/roles/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchRoles();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div data-testid="roles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Roles</h1>
          <p className="text-slate-500 mt-1">Manage roles and permission matrices</p>
        </div>
        {perms.canAdd && (
          <button
            onClick={() => navigate('/admin/org-data/roles/new')}
            data-testid="add-role-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-6 py-2.5 rounded-none hover:bg-orange-600 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Role
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200">
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-3">Role Name</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-4">Description</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Type</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Created</div>
            <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-1">Actions</div>
          </div>

          {roles.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No roles found</div>
          ) : (
            roles.map((role) => (
              <div
                key={role.id}
                data-testid={`role-row-${role.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                {/* Name */}
                <div className="col-span-3 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="font-medium text-slate-900">{role.name}</span>
                </div>

                {/* Description */}
                <div className="col-span-4 text-slate-600 text-sm">{role.description || '-'}</div>

                {/* Type badge */}
                <div className="col-span-2">
                  {role.is_predefined ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                      <Lock className="h-3 w-3" /> Predefined
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                      <Users className="h-3 w-3" /> Custom
                    </span>
                  )}
                </div>

                {/* Created date */}
                <div className="col-span-2 text-slate-500 text-sm">{formatDate(role.created_at)}</div>

                {/* Actions */}
                <div className="col-span-1 flex items-center gap-1">
                  {!role.is_predefined && perms.canEdit && (
                    <button
                      onClick={() => navigate(`/admin/org-data/roles/${role.id}/edit`)}
                      data-testid={`edit-role-${role.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  )}
                  {!role.is_predefined && perms.canDelete && (
                    <button
                      onClick={() => { setDeleteConfirm(role); setDeleteError(''); }}
                      data-testid={`delete-role-${role.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Role?</h3>
            <p className="text-slate-600 mb-2 text-sm">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            {deleteError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm mb-3 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />{deleteError}
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteError(''); }}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                data-testid="confirm-delete-role-btn"
                className="px-4 py-2 bg-red-600 text-white font-bold rounded-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
